import { CommonModule, NgTemplateOutlet, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgModule,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'sw-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [NgTemplateOutlet, NgClass, FormsModule],
})
export class SwapPaginationComponent implements OnChanges {
  @Input() total!: number;
  @Input() page: number = 1;
  @Input() pageNumber: number = 1;
  @Input() showNumberOfItems: number = 3;
  @Input() showInput = true;
  @Input() buttonStyle!: string;
  @Input() itemsTemplate!: TemplateRef<any>;
  @Input() maxSize: number = 3;
  @Input() alignItems!: any;
  @Input() showNumberText: boolean = false;

  @Output() onPageChange = new EventEmitter();
  @Output() onPageSizeChange = new EventEmitter();
  pageSizeCopy!: number;
  pageNumbers: any[] = [];
  isDot = false;
  isShowEndDot = false;
  allPagesArr: number[] = [];
  pageArr: number[] = [];
  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this.pageNumber = this.page;
    if (this.showNumberOfItems > this.total) {
      this.pageSizeCopy = this.showNumberOfItems;
      this.showNumberOfItems = this.total;
    } else if (changes && changes['showNumberOfItems']) {
      this.showNumberOfItems = changes['showNumberOfItems']['currentValue'];
      this.pageSizeCopy = this.showNumberOfItems;
    } else {
      if (
        changes &&
        changes['showNumberOfItems'] &&
        changes['showNumberOfItems']['firstChange']
      ) {
        // do nothing
      } else {
        this.showNumberOfItems = this.pageSizeCopy;
      }
    }
    const totalPages = Math.ceil(this.total / this.showNumberOfItems);
    this.pageArr =
      this.maxSize >= totalPages
        ? [...Array(totalPages).keys()].map(x => x + 1)
        : [...Array(this.maxSize).keys()].map(x => x + 1);
    this.allPagesArr = [...Array(totalPages).keys()].map(x => x + 1);
    this.displayPaginationNumber();
  }

  displayPaginationNumber() {
    const totalPages = Math.ceil(this.total / this.showNumberOfItems);
    let finalArray = [];
    const currentPageIndex = this.allPagesArr.indexOf(this.page);
    if (
      !this.pageArr.includes(this.page) &&
      this.page > this.pageArr[this.pageArr.length - 1]
    ) {
      let updatedArr = [];
      for (let i = 0; i < this.maxSize; i++) {
        const num = this.allPagesArr[currentPageIndex + i];
        if (num) {
          updatedArr.push(num);
        }
      }
      this.pageArr = [...updatedArr];
    } else if (
      !this.pageArr.includes(this.page) &&
      this.page < this.pageArr[0]
    ) {
      let updatedArr = [];
      for (let i = 0; i < this.maxSize; i++) {
        const num = this.allPagesArr[currentPageIndex - i];
        if (num) {
          updatedArr.push(num);
        }
      }
      this.pageArr = [...updatedArr.reverse()];
    }
    finalArray = [...this.pageArr];
    const firstElement = this.pageArr[0];
    const lastElement = this.pageArr[this.pageArr.length - 1];
    const indexOfFirstElement = this.allPagesArr.indexOf(firstElement);
    const indexOfLastElement = this.allPagesArr.indexOf(lastElement);
    if (indexOfFirstElement > 2) {
      this.isDot = true;
    } else {
      this.isDot = false;
    }
    if (indexOfLastElement < this.allPagesArr.length - 3) {
      this.isShowEndDot = true;
    } else {
      this.isShowEndDot = false;
    }
    if (this.isDot) {
      finalArray = [1, '...', ...this.pageArr];
    } else {
      if (this.pageArr[0] > 2) {
        finalArray = [
          this.pageArr[0] - 2,
          this.pageArr[0] - 1,
          ...this.pageArr,
        ];
      } else {
        finalArray = [...this.pageArr];
      }
    }
    if (this.isShowEndDot) {
      finalArray = [
        ...finalArray,
        '...',
        this.allPagesArr[this.allPagesArr.length - 1],
      ];
    } else {
      const updatedArr = [];
      for (let i = indexOfLastElement + 1; i < this.allPagesArr.length; i++) {
        updatedArr.push(this.allPagesArr[i]);
      }
      finalArray = [...finalArray, ...updatedArr];
    }

    this.pageNumbers = [...finalArray];
  }

  previous() {
    this.page = this.page - 1;
    this.pageNumber = this.page;
    this.displayPaginationNumber();
    this.onPageChange.next({
      page: this.page,
    });
  }

  next() {
    this.page = this.page + 1;
    this.pageNumber = this.page;
    this.onPageChange.next({
      page: this.page,
    });
    this.displayPaginationNumber();
  }

  start() {
    if (this.page === 1) {
      this.pageNumber = 1;
      return '1';
    } else {
      return 1 + this.page * this.showNumberOfItems - this.showNumberOfItems;
    }
  }

  end() {
    if (this.page === 1) {
      return this.showNumberOfItems;
    } else if (this.page * this.showNumberOfItems >= this.total) {
      return this.total;
    } else {
      return this.page * this.showNumberOfItems;
    }
  }
  pagenumberClick(pageNum: number) {
    this.page = pageNum;
    this.pageNumber = this.page;
    this.displayPaginationNumber();
    this.onPageChange.next({
      page: this.page,
    });
  }
  previousDisabled() {
    return this.page <= 1;
  }

  nextDisabled() {
    return this.total === this.end();
  }

  test() {
    return 'test';
  }

  enterPressed(pageNumber: number) {
    if (pageNumber) {
      const regex = new RegExp(/^\d+$/);
      if (!regex.test(pageNumber.toString())) {
        this.pageNumber = this.page;
        return;
      }
      this.pageNumber = parseInt(pageNumber.toString());
      if (pageNumber < 1) {
        this.pageNumber = this.page;
        return;
      }
      if (this.pageNumber * this.showNumberOfItems > this.total) {
        this.pageNumber = this.page;
        return;
      }

      this.page = this.pageNumber;
    }
  }
}
